import React, { useEffect, useState } from "react";
import { isArray, isEmpty } from "lodash";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Setting } from "../../utils/Setting";
import { getApiData } from "../../utils/APIHelper";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import { ReactComponent as BlackSmallDhiramIcon } from "../../assets/svg/BlackSmallDhiramIcon.svg";
import { Box, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { statusStyleArray } from "./config";
import tabs from "./config";
import rawStyles from "./styles";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";
import Milestones from "./components/Milestones";
import PaymentGroups from "./components/PaymentGroups";
import Changelog from "../OnGoing/Changelog";
import ProjectOverview from "./components/ProjectOverview";
import ContractorPayments from "./components/ContractorPayments";
import PaymentsBilling from "./components/PaymentsBilling";
import Design from "./components/Design";
import Inspections from "./components/Inspections";
import ProjectUpdates from "./components/ProjectUpdates";
import ProjectLogIssues from "./components/ProjectLogIssues";
import Rooms from "./components/Rooms";
import Budgets from "./components/Budgets";
import Scopes from "./components/Scopes";
import { useMediaQuery, useTheme } from "@mui/material";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import Schedule from "./components/Schedule";
import { getProjectsListPath } from "../Projects/utils/projectViewHelpers";
import { renderCurrencyIcon } from "../../utils/CurrencyUtils";
import ProjectRoster from "../WorkforceManagementTab";
import ContractorProjectDetails from "../ContractorProjectDetails";

const StyledTab = styled(Tab)(({ theme, selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d !important" : "#fff !important",
  border: "1px solid #d7d8d9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "4px 16px !important",
  fontFamily: "Inter Tight",
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
  "&.Mui-disabled": {
    opacity: 0.7,
    cursor: "not-allowed !important",
    backgroundColor: "#f5f5f5 !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent",
  },
}));

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

export default function NewProjectDetails() {
  const [routeSearchParams] = useSearchParams();
  if (routeSearchParams.get("managed_by") === "contractor") {
    return <ContractorProjectDetails />;
  }
  return <NewProjectDetailsImpl />;
}

function NewProjectDetailsImpl() {
  const [loader, setLoader] = useState(false);
  const [details, setDetails] = useState({});
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Token validation hook
  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  const tabRoutes = [
    "",
    "design",
    "rooms",
    "budgets",
    "scopes",
    "schedule",
    "billing-milestones",
    "payments",
    "change-request",
    "inspections",
    "issues",
    "project-updates",
    "workforce-management",
  ];

  const tabValue = tabRoutes.indexOf(tab || "");
  const styles = rawStyles(isMobile);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    getProjectDetails();
  }, [id]);

  async function getProjectDetails() {
    setLoader(true);
    try {
      const response = await getApiData(
        `${Setting.endpoints.getProject}?proposal_id=${id}`,
        "GET",
        {},
      );
      if (
        response.success &&
        isArray(response?.data) &&
        !isEmpty(response?.data)
      ) {
        setDetails(response?.data[0]);
      }
      setLoader(false);
    } catch (error) {
      console.log("error:===>>>", error);
      setLoader(false);
    }
  }

  function handleTabChange(event, newValue) {
    const newTabRoute = tabRoutes[newValue]
      ? `/project/details/${id}/${tabRoutes[newValue]}`
      : `/project/details/${id}`;

    // Preserve from/view (and other) query params across tab switches.
    const qs = searchParams.toString();
    navigate(qs ? `${newTabRoute}?${qs}` : newTabRoute);
  }

  const changeId = searchParams.get("change");

  function renderTabContent() {
    console.log("tabValue", tabValue);
    switch (tabValue) {
      case 0:
        return (
          <ProjectOverview
            details={details}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 1:
        return <Design details={details} isMobile={isMobile} />;
      case 2:
        return (
          <Rooms details={details} isMobile={isMobile} passedStyles={styles} />
        );
      case 3:
        return (
          <Budgets details={details} isMobile={isMobile} styles={styles} />
        );
      case 4:
        return (
          <Scopes details={details} isMobile={isMobile} passedStyles={styles} />
        );
      case 5:
        return (
          <Schedule
            details={details}
            isMobile={isMobile}
            passedStyles={styles}
          />
        );
      case 6:
        return <PaymentsBilling projectId={details?.id} proposalId={id} />;
      case 7:
        return (
          <ContractorPayments
            projectId={details?.id}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 8:
        return (
          <Changelog
            projectId={details?.id}
            isMobile={isMobile}
            styles={styles}
            projectDetails={details}
          />
        );
      case 9:
        return (
          <Inspections
            projectId={details?.id}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 10:
        return (
          <ProjectLogIssues
            projectId={details?.id}
            projectDetails={details}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 11:
        return (
          <ProjectUpdates
            projectId={details?.id}
            projectDetails={details}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 12:
        return <ProjectRoster projectId={details?.id} />;
      default:
        return <div>Default Content</div>;
    }
  }

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Security Policy Modal */}
      <SecurityPolicyModal
        open={showSecurityModal}
        onConfirm={handleSecurityModalClose}
      />

      {loader ? (
        <center style={styles.header}>
          <CircularProgress />
        </center>
      ) : (
        <div
          style={{
            ...styles.container,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <header style={{ ...styles.header, flexShrink: 0 }}>
            <div style={styles.headerContent}>
              <LeftArrowIcon
                onClick={() => {
                  if (searchParams.get("from") === "projects") {
                    navigate(getProjectsListPath(searchParams.get("view")));
                  } else {
                    navigate("/dashboard");
                  }
                }}
                style={{ cursor: "pointer" }}
              />
              <div style={styles.idText}>
                <span style={styles.idLabel}>ID:</span> {details?.project_slug}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Box
                  style={{
                    width: "15px",
                    height: "15px",
                    borderRadius: "50%",
                    backgroundColor:
                      statusStyleArray.find(
                        (style) => style.status === details?.status,
                      )?.backgroundColor || "transparent",
                    border: "3px solid #e5e5e5",
                  }}
                />
                <Typography
                  style={{
                    fontSize: "14px",
                    marginLeft: "8px",
                    color: "#0D0D0E",
                  }}
                >
                  {getStatusDisplayText(details?.status)}
                </Typography>
              </div>
            </div>
          </header>

          <div
            style={{
              ...styles.subContainer,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile && "2rem",
                flexShrink: 0,
              }}
            >
              <div style={{ ...styles.rowFlex, gap: "15px", flex: "2" }}>
                <Box
                  component="img"
                  src={details?.default_image?.[0]?.image}
                  alt="Project Icon"
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: "16px",
                    marginRight: 1,
                  }}
                />
                <div style={styles.columnFlex}>
                  <div style={{ ...styles.rowFlex, gap: "15px" }}>
                    <p style={styles.startDate}>
                      Start Date: {details?.start_date}
                    </p>
                  </div>
                  <h2 style={styles.projectHeading}>{details?.name}</h2>
                  <p style={styles.endDate}>Moving Date: {details?.end_date}</p>
                </div>
              </div>

              <div style={{ ...styles.columnFlex, flex: "1" }}>
                <p style={styles.idLabel}>Cost</p>
                <p style={styles.price}>
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    {renderCurrencyIcon("black-small")}
                    {formattedAmountWithoutAed(
                      details?.proposal_status !== "ongoing"
                        ? details?.budget
                        : details?.budget_without_commission,
                    )}
                  </span>
                </p>
              </div>
            </div>

            {!changeId && (
              <Tabs
                value={tabValue}
                variant="scrollable"
                onChange={handleTabChange}
                sx={{
                  marginTop: "3vh",
                  flexShrink: 0,
                  "& .MuiTabs-indicator": {
                    background: "none",
                  },
                  "& .MuiTabs-scrollButtons": {
                    display: "none",
                  },
                  "& .MuiTabs-scroller": {
                    scrollbarWidth: "none",
                  },
                  "& .MuiTabs-scroller::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                {tabs?.map((tab, index) => (
                  <StyledTab
                    key={tab.id}
                    label={tab.title}
                    selected={tabValue === index}
                    disabled={index === 9 && details?.status !== "ongoing"}
                  />
                ))}
              </Tabs>
            )}

            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                marginTop: "1vh",
              }}
            >
              {renderTabContent()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
